import { useState } from 'react';
import { Animated, Pressable, Text, View } from 'react-native';
import { Image } from 'expo-image';
import { IDLE_COIN_SOURCE } from '../../constants/assets';
import { LayeredAvatarView } from '../avatars/LayeredAvatarLab/LayeredAvatarView';
import {
  ONBOARDING_CLIMB,
  ONBOARDING_COLORS,
  type OnboardingLeaderboardRow,
} from '../../src/tutorial/constants/onboardingContent.constants';
import {
  DEFAULT_ONBOARDING_FIT,
  ONBOARDING_GEAR,
  isGearActive,
  toggleOnboardingGear,
} from '../../src/tutorial/helpers/onboardingGear.helper';
import type { EquippedMap } from '../avatars/LayeredAvatarLab/characterCustomizer.types';
import { EXPO_IMAGE_ALT_DECORATIVE } from '../../src/shared/constants/expoImageAlt.constants';
import { useTutorialEntrance } from '../../src/tutorial/hooks/useTutorialEntrance';
import { OnboardingHeadline } from './OnboardingHeadline';
import type { OnboardingSceneProps } from './onboardingScene.types';
import { flexArtStyles as styles } from './FlexArt.styles';

const MONOGRAM_TONE: Record<'green' | 'red', { bg: string; fg: string }> = {
  green: { bg: ONBOARDING_COLORS.green, fg: ONBOARDING_COLORS.mint },
  red: { bg: ONBOARDING_COLORS.redDeep, fg: ONBOARDING_COLORS.red },
};

function LeaderboardRowView({
  row,
  equipped,
}: {
  row: OnboardingLeaderboardRow;
  equipped: EquippedMap;
}) {
  const isYou = row.tone === 'you';
  return (
    <View style={[styles.lbRow, isYou && styles.lbRowYou]}>
      <Text style={[styles.lbRank, isYou && styles.lbRankYou]}>{row.rank}</Text>
      {row.tone === 'you' ? (
        <View style={styles.lbAvatar}>
          <LayeredAvatarView equipped={equipped} size={30} rounded />
        </View>
      ) : (
        <View style={[styles.lbMonogram, { backgroundColor: MONOGRAM_TONE[row.tone].bg }]}>
          <Text style={[styles.lbMonogramText, { color: MONOGRAM_TONE[row.tone].fg }]}>
            {row.initials}
          </Text>
        </View>
      )}
      <Text style={[styles.lbName, isYou && styles.lbNameYou]} numberOfLines={1}>
        {row.name}
      </Text>
      {row.delta ? <Text style={styles.lbDelta}>{row.delta}</Text> : null}
      <Image
        source={IDLE_COIN_SOURCE}
        style={styles.lbCoin}
        contentFit="contain"
        alt={EXPO_IMAGE_ALT_DECORATIVE}
      />
      <Text style={styles.lbCoins}>{row.coins}</Text>
    </View>
  );
}

/** Screen 3 — Climb: live layered mascot + gear toggles + daily leaderboard. */
export function FlexArt({ isActive, layout, headline, subheadline }: OnboardingSceneProps) {
  const [equipped, setEquipped] = useState<EquippedMap>(DEFAULT_ONBOARDING_FIT);
  const stage = useTutorialEntrance(isActive, 120);
  const board = useTutorialEntrance(isActive, 220);

  return (
    <View style={styles.scene}>
      <OnboardingHeadline
        isActive={isActive}
        title={headline}
        subtitle={subheadline}
        subtitleVariant="accentLine"
        subtitleColor={ONBOARDING_COLORS.mint}
      />

      <Animated.View
        style={[
          styles.mascotStage,
          { width: layout.climbMascot, height: layout.climbMascot },
          { opacity: stage.opacity, transform: [{ scale: stage.scale }] },
        ]}
      >
        <LayeredAvatarView equipped={equipped} size={layout.climbMascot} rounded={false} />
      </Animated.View>

      <View style={styles.gearGrid}>
        {ONBOARDING_GEAR.map((gear) => {
            const on = isGearActive(equipped, gear);
            return (
              <Pressable
                key={gear.key}
                onPress={() => setEquipped((prev) => toggleOnboardingGear(prev, gear))}
                accessibilityRole="button"
                accessibilityState={{ selected: on }}
                testID={`onboarding-gear-${gear.key}`}
                style={({ pressed }) => [
                  styles.gearCell,
                  on ? styles.gearCellOn : styles.gearCellOff,
                  pressed && styles.gearCellPressed,
                ]}
              >
                <View style={[styles.gearDot, { backgroundColor: on ? ONBOARDING_COLORS.mint : '#4B5563' }]} />
                <Text style={[styles.gearLabel, on ? styles.gearLabelOn : styles.gearLabelOff]}>
                  {gear.label}
                </Text>
              </Pressable>
            );
          })}
      </View>

      <Text style={styles.gearHint}>{ONBOARDING_CLIMB.gearHint}</Text>

      <Animated.View
        style={[styles.lbCard, { opacity: board.opacity, transform: [{ scale: board.scale }] }]}
      >
        <View style={styles.lbHeader}>
          <Text style={styles.lbHeaderLabel}>{ONBOARDING_CLIMB.boardLabel}</Text>
          <Text style={styles.lbClimbing}>{ONBOARDING_CLIMB.climbingLabel}</Text>
        </View>
        {ONBOARDING_CLIMB.rows.map((row) => (
          <LeaderboardRowView key={row.name} row={row} equipped={equipped} />
        ))}
      </Animated.View>
    </View>
  );
}
